import React, { lazy, memo, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity, BarChart3, BellRing, ChevronRight, Clock3, Crosshair, ListChecks, Radio, Trophy, Zap,
} from "lucide-react";
import {
  fetchBuyerSignal, fetchSentiment, fetchOI, fetchMagnitude, fetchBuyerEdge,
  fetchAlertsFeed, fetchEvents, fetchTrendRegime, fetchDayRegime,
} from "../api";
import { openStream } from "../sse";
import { useTraderStatus } from "../context/TraderStatusContext";
import { usePolling } from "../hooks/usePolling";
import { clamp, fmtPrice, Panel } from "../components/dashboard/shared";
import BuyerFunnel from "../components/dashboard/BuyerFunnel";
import CallPutSplit from "../components/dashboard/CallPutSplit";
import ExpiryStrip from "../components/dashboard/ExpiryStrip";
import SessionTimeline from "../components/dashboard/SessionTimeline";
import AlertsPanel, { SystemAlertStrip } from "../components/dashboard/AlertsPanel";
import PostEntryCockpit, { CockpitRiskBand, managePathFor } from "../components/dashboard/Cockpit";
import PremiumTracker from "../components/dashboard/PremiumTracker";
import StrikeHeatmap from "../components/dashboard/StrikeHeatmap";
import { BuyerChecklist, SmartStrikeSelector } from "../components/dashboard/DecisionDetail";
import { applyRuleExits, directionVerdict, ruleForSide } from "../components/dashboard/VerdictCard";
import { simpleAction, simpleRegime } from "../components/dashboard/SimpleSignal";

/* The IV smile rides on recharts; it only mounts inside the chain tab. */
const IvSmile = lazy(() => import("../components/dashboard/PremiumChart").then((m) => ({ default: m.IvSmile })));

export { managePathFor };

/*
 * OPTION BUYER DASHBOARD (2026-10-06 reorganisation)
 *
 * The screen is built for one question — "should I buy an option right now,
 * and which one?" — in the order the research earned:
 *
 *   1  Buyer funnel   four gates (open? moving? can the move pay? side?) → ONE
 *                     verdict, the order ticket or the one thing to change.
 *   2  Open positions the post-entry cockpit (only while something is open).
 *   3  CALL vs PUT    what each side costs and whether today's move can pay.
 *   4  Everything else on the same screen: entry checklist + strike ranking,
 *                     premium trace, option chain + IV smile, system alerts.
 *
 * Dropped from the first screen (no measured edge for a buyer, see the
 * research notes): market breadth, the direction-only buyer console, the
 * per-side historical edge, and the local breakout-alert log.
 */

function getIndiaClock(atMs = Date.now()) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", weekday: "short", hour12: false,
  }).formatToParts(new Date(atMs));
  const hour = Number(parts.find((p) => p.type === "hour")?.value || 0);
  const minute = Number(parts.find((p) => p.type === "minute")?.value || 0);
  const weekday = parts.find((p) => p.type === "weekday")?.value || "";
  return { minuteOfDay: hour * 60 + minute, weekday, label: `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}` };
}

function getTradingWindow(minuteOfDay, weekday = "") {
  const open = 9 * 60 + 15;
  const close = 15 * 60 + 30;
  if (["Sat", "Sun"].includes(weekday)) {
    return { state: "CLOSED", tone: "slate", label: "Weekend", detail: "Market is closed; use the last snapshot only for preparation." };
  }
  if (minuteOfDay < open || minuteOfDay >= close) {
    return { state: "CLOSED", tone: "slate", label: "Market closed", detail: "Prepare levels; do not chase pre-open indications." };
  }
  if (minuteOfDay < 9 * 60 + 25) {
    return { state: "AVOID", tone: "red", label: "Opening noise", detail: "Let the first range and spreads settle." };
  }
  if (minuteOfDay <= 11 * 60 + 30) {
    return { state: "BEST", tone: "emerald", label: "Primary window", detail: "Liquidity and directional follow-through are usually strongest." };
  }
  if (minuteOfDay < 13 * 60 + 35) {
    return { state: "AVOID", tone: "amber", label: "Midday decay", detail: "Low follow-through makes theta relatively expensive." };
  }
  if (minuteOfDay <= 14 * 60 + 45) {
    return { state: "GOOD", tone: "cyan", label: "Second window", detail: "Trade only confirmed range expansion." };
  }
  return { state: "AVOID", tone: "red", label: "Late-session risk", detail: "Fast theta and reversal risk; reduce holding time." };
}

/* The few locally-derived facts the panels still read: ATM premium momentum
 * (premium trace), the volatility word and the clock window (cockpit
 * warnings), spot, and a default side for the strike ranking. Everything that
 * decides the verdict comes from the server-owned buyer signal below. */
function baseView(optionData, vix, clock) {
  const ceMomentum = Number(optionData?.premium_momentum?.ce_pct || 0);
  const peMomentum = Number(optionData?.premium_momentum?.pe_pct || 0);
  const callStrength = Number(optionData?.call_strength ?? 50);
  const putStrength = Number(optionData?.put_strength ?? 50);
  const spot = Number(optionData?.spot || 0);
  const timeWindow = getTradingWindow(clock.minuteOfDay, clock.weekday);
  const vixMove = Number(vix?.change_pct || 0);
  const ivMove = Number(optionData?.atm_iv_change || 0);
  const volatility = ivMove > 0.35 || vixMove > 1 ? { label: "EXPANDING", tone: "emerald" }
    : ivMove < -0.35 || vixMove < -1 ? { label: "COMPRESSING", tone: "red" }
      : { label: "STABLE", tone: "amber" };
  return {
    ceMomentum, peMomentum, spot, timeWindow, volatility,
    side: callStrength >= putStrength ? "CE" : "PE",
    direction: "NEUTRAL", risk: 0, riskFlags: [], tradePlan: null, health: { ready: false, sources: {} },
  };
}

function applyCanonicalSignal(base, signal) {
  if (!signal) {
    return {
      ...base,
      direction: "NO DATA", directionTone: "slate", confirmation: 0, setupScore: 0,
      action: { side: "AVOID", label: "SIGNAL SERVER OFFLINE", detail: "Waiting for the server-owned data-health gate", tone: "red" },
      regime: { label: "DATA NOT READY", detail: "No trade until every required source is fresh", tone: "slate", buyerFriendly: false },
      risk: 100, riskFlags: ["Canonical signal unavailable"], tradePlan: null,
      health: { ready: false, blockers: ["SIGNAL_SERVER_UNAVAILABLE"], sources: {} },
      technical: {}, canonicalChecks: {},
      confidence: null, quality: null, reason: "", checklist: [], noTradeReasons: [], invalidation: "", calibration: null,
    };
  }
  const side = signal.action?.side || "WAIT";
  const actionTone = side === "CALL" ? "emerald" : side === "PUT" ? "violet" : side === "AVOID" ? "red" : "amber";
  const directionTone = signal.direction === "BULLISH" ? "emerald" : signal.direction === "BEARISH" ? "violet" : "amber";
  const regimeRaw = signal.regime?.label || "TRANSITION";
  const regimeLabel = regimeRaw.replaceAll("_", " ");
  const regimeBuyerFriendly = signal.action?.actionable || (regimeRaw.includes("TRENDING") && signal.health?.ready);
  const regimeTone = !signal.health?.ready ? "red" : regimeRaw.includes("BULLISH") ? "emerald" : regimeRaw.includes("BEARISH") ? "violet" : regimeRaw.includes("VOLATILE") ? "cyan" : "amber";
  const blockers = signal.blockers || [];
  const warnings = signal.warnings || [];
  const plan = signal.trade_plan ? {
    side: signal.trade_plan.side,
    optionType: signal.trade_plan.option_type,
    strike: signal.trade_plan.strike,
    quality: signal.trade_plan.setup_score,
    current: signal.trade_plan.current,
    vwap: signal.trade_plan.premium_vwap,
    entry: signal.trade_plan.entry,
    stop: signal.trade_plan.premium_stop,
    target1: signal.trade_plan.target_1,
    target2: signal.trade_plan.target_2,
    spotInvalidation: signal.trade_plan.spot_stop,
    spotTarget1: signal.trade_plan.spot_target_1,
    spotTarget2: signal.trade_plan.spot_target_2,
    rewardRisk1: signal.trade_plan.reward_risk_1,
    rewardRisk2: signal.trade_plan.reward_risk_2,
    lotSize: signal.trade_plan.lot_size,
    lots: signal.trade_plan.lots,
    quantity: signal.trade_plan.quantity,
    premiumOutlay: signal.trade_plan.premium_outlay,
    riskRupees: signal.trade_plan.risk_rupees,
    rewardRupeesT1: signal.trade_plan.reward_rupees_t1,
    estCosts: signal.trade_plan.est_costs_rupees,
    estSlippage: signal.trade_plan.est_slippage_rupees,
    breakevenPremium: signal.trade_plan.breakeven_premium,
    netRiskRupees: signal.trade_plan.net_risk_rupees,
    netRewardRisk1: signal.trade_plan.net_reward_risk_1,
    timeStop: `Exit if premium does not expand within ${signal.trade_plan.time_stop_minutes} min`,
    flags: signal.trade_plan.flags || [],
  } : null;
  const headlineScore = signal.quality?.total ?? signal.setup_score ?? 0;
  return {
    ...base,
    direction: signal.direction || "NEUTRAL",
    directionTone,
    confirmation: headlineScore,
    setupScore: headlineScore,
    side: signal.direction === "BULLISH" ? "CE" : signal.direction === "BEARISH" ? "PE" : base.side,
    action: { ...signal.action, tone: actionTone },
    regime: { label: regimeLabel, detail: regimeBuyerFriendly ? "Price, chain, liquidity and data-health gates are aligned" : (blockers[0] || "Wait for aligned buyer conditions").replaceAll("_", " "), tone: regimeTone, buyerFriendly: regimeBuyerFriendly },
    risk: signal.health?.ready ? clamp(100 - headlineScore + blockers.length * 12) : 100,
    riskFlags: [...blockers, ...warnings].map((item) => String(item).replaceAll("_", " ")),
    tradePlan: plan,
    technical: signal.technical || {},
    health: signal.health || { ready: false, sources: {} },
    canonicalChecks: signal.checks || {},
    confidence: signal.confidence || null,
    quality: signal.quality || null,
    reason: signal.reason || "",
    checklist: signal.checklist || [],
    noTradeReasons: signal.no_trade_reasons || [],
    invalidation: signal.invalidation || "",
    calibration: signal.calibration || null,
  };
}

/* Session overlay. The local clock only knows weekends and hours; exchange
 * holidays come from the buyer-edge calendar (trading_calendar.py). */
export function dayOffFromEdge(edge) {
  if (!edge) return null;
  const cal = edge.calendar;
  const blockers = edge.verdict?.blockers || [];
  if (blockers.includes("MARKET_HOLIDAY") || (cal?.trading_day === false && cal?.holiday)) return "HOLIDAY";
  if (blockers.includes("WEEKEND") || cal?.trading_day === false) return "WEEKEND";
  return null;
}

export function applySessionOverlay(view, dayOff = null) {
  const holiday = dayOff === "HOLIDAY";
  if (!dayOff && view.timeWindow?.state !== "CLOSED") return { ...view, sessionClosed: false, closedLabel: null };
  const label = holiday ? "Market holiday" : dayOff === "WEEKEND" ? "Weekend" : (view.timeWindow?.label || "Market closed");
  const detail = holiday ? "Exchange holiday — no session today; the last snapshot is for planning only."
    : (view.timeWindow?.detail || "Market closed");
  return {
    ...view,
    sessionClosed: true,
    closedLabel: label,
    action: {
      side: "AVOID", label: holiday ? "MARKET HOLIDAY" : "MARKET CLOSED",
      detail: "Last session snapshot is available for planning only", tone: "slate", actionable: false,
    },
    reason: holiday ? "Intraday signals resume on the next trading day" : "Intraday signals resume when the market session opens",
    risk: 0,
    riskFlags: [label],
    tradePlan: null,
    noTradeReasons: [detail],
    invalidation: holiday ? "Market holiday" : "Session closed",
  };
}

/* One screen (2026-10-09, user's ask): every section is visible, stacked in
 * the order a buyer reads it. Nothing is behind a switch or a tab. The
 * recharts subtrees still load lazily so the verdict never waits on them. */

const INDEX_INK = { emerald: "text-emerald-300", rose: "text-rose-300", amber: "text-amber-300", slate: "text-slate-500" };

// A stream with neither data nor the server's 5 s heartbeat for this long is
// reopened (sse.js), and data this old marks the feed chip DELAYED.
const STREAM_STALE_MS = 15000;

export default function Dashboard() {
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState("NIFTY");
  const [sentiment, setSentiment] = useState(null);
  const [oi, setOi] = useState(null);
  const [magnitude, setMagnitude] = useState(null);
  const [trendRegime, setTrendRegime] = useState(null);  // /trend-regime/current, 30 s, per index (UP / DOWN / SIDEWAYS)
  const [dayRegime, setDayRegime] = useState(null);      // /dashboard/day-regime, 60 s, per index (info only)
  const [buyerEdge, setBuyerEdge] = useState(null);      // /dashboard/buyer-edge, 10 s, per index
  const [alertsFeed, setAlertsFeed] = useState(null);    // /dashboard/alerts, 15 s
  const [events, setEvents] = useState(null);            // /dashboard/events, 30 min (changes daily)
  const [buyerSignals, setBuyerSignals] = useState(null);
  const trader = useTraderStatus();          // polled once by the app shell, every 5 s
  const [connected, setConnected] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [premiumSeries, setPremiumSeries] = useState({ NIFTY: [], BANKNIFTY: [] });
  const [desktopAlerts, setDesktopAlerts] = useState(false);
  // When each feed last delivered, kept apart: one shared clock was reset every
  // second by the sentiment stream, so the chip read "LIVE · 0s" while a stalled
  // chain or verdict sat frozen on screen.
  const lastSentimentUpdate = useRef(0);
  const lastOiUpdate = useRef(0);
  const lastBuyerUpdate = useRef(0);
  const lastDesktopAlert = useRef("");

  const acceptOI = useCallback((payload) => {
    setOi(payload);
    lastOiUpdate.current = Date.now();
    setPremiumSeries((current) => {
      const next = { ...current };
      ["NIFTY", "BANKNIFTY"].forEach((key) => {
        const item = payload?.indices?.[key];
        if (!item?.available || (item?.atm_premiums?.ce == null && item?.atm_premiums?.pe == null)) return;
        // The ATM rows' premium VWAP rides along so the chart can draw each
        // side against the line the rules confirm entries on.
        const atm = Number(item.atm || 0);
        const atmRows = (item.chain || []).filter((row) => Number(row.strike) === atm);
        const ceRow = atmRows.find((row) => row.option_type === "CE");
        const peRow = atmRows.find((row) => row.option_type === "PE");
        const point = {
          time: new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }).format(new Date()),
          ce: item.atm_premiums.ce, pe: item.atm_premiums.pe,
          ceVwap: ceRow?.premium_vwap ?? null, peVwap: peRow?.premium_vwap ?? null,
          spot: item.spot != null && !Number.isNaN(Number(item.spot)) ? Number(item.spot) : null,
        };
        next[key] = [...(current[key] || []), point].slice(-45);
      });
      return next;
    });
  }, []);

  // A feed's GET, at most one in flight (a Dhan chain build takes ~5 s), unless
  // that feed delivered within `quietMs`.
  const inFlight = useRef({});
  const pollIfQuiet = useCallback((key, lastRef, quietMs, fetcher, accept) => {
    if (inFlight.current[key] || Date.now() - lastRef.current < quietMs) return;
    inFlight.current[key] = true;
    fetcher()
      .then((r) => { accept(r.data); lastRef.current = Date.now(); })
      .catch(() => {})
      .finally(() => { inFlight.current[key] = false; });
  }, []);

  useEffect(() => {
    // The three streams below paint the first snapshot themselves: the server
    // pushes its latest one the moment a client connects. The GETs are a
    // fallback only, for a proxy that buffers SSE or a stream that failed to open
    // — checked per stream, so a quick sentiment frame no longer cancels the
    // chain's fallback.
    const fallback = setTimeout(() => {
      if (!lastSentimentUpdate.current) pollIfQuiet("sentiment", lastSentimentUpdate, 0, fetchSentiment, setSentiment);
      if (!lastOiUpdate.current) pollIfQuiet("oi", lastOiUpdate, 0, fetchOI, acceptOI);
      if (!lastBuyerUpdate.current) pollIfQuiet("buyer", lastBuyerUpdate, 0, fetchBuyerSignal, setBuyerSignals);
    }, 1500);
    return () => { clearTimeout(fallback); };
  }, [acceptOI, pollIfQuiet]);

  // Every timer below runs through usePolling: paused while the tab is
  // hidden, re-run the moment it is visible again.
  const loadSecondary = useCallback(() => {
    // The 5-minute tail gate only moves every 5 minutes; 15 s here against the
    // endpoint's own 10 s cache is already far more often than it can change.
    fetchMagnitude().then((r) => setMagnitude(r.data)).catch(() => {});
    fetchAlertsFeed(20).then((r) => setAlertsFeed(r.data)).catch(() => {});
  }, []);
  usePolling(loadSecondary, 15000);
  const loadEvents = useCallback(() => {
    fetchEvents(30).then((r) => setEvents(r.data?.events || [])).catch(() => {});
  }, []);
  usePolling(loadEvents, 30 * 60 * 1000);

  // Per-index feeds: cached server-side, polled at that cadence and re-fetched
  // the moment the index switches. A payload for another index is ignored, so
  // the old index's numbers never show under the new index's name.
  const edgeIndexRef = useRef(activeIndex);
  edgeIndexRef.current = activeIndex;
  const loadBuyerEdge = useCallback(() => {
    const want = activeIndex;
    fetchBuyerEdge(want)
      .then((r) => { if (edgeIndexRef.current === want) setBuyerEdge(r.data); })
      .catch(() => {});
  }, [activeIndex]);
  useEffect(() => { loadBuyerEdge(); }, [loadBuyerEdge]);   // index switch: now
  usePolling(loadBuyerEdge, 10000, { immediate: false });

  // Trend regime moves on closed 5-minute bars; a failed read clears it so the
  // side gate falls back to the signal's direction instead of a stale state.
  const loadTrendRegime = useCallback(() => {
    const want = activeIndex;
    fetchTrendRegime(want)
      .then((r) => { if (edgeIndexRef.current === want) setTrendRegime(r.data); })
      .catch(() => { if (edgeIndexRef.current === want) setTrendRegime(null); });
  }, [activeIndex]);
  useEffect(() => { loadTrendRegime(); }, [loadTrendRegime]);
  usePolling(loadTrendRegime, 30000, { immediate: false });

  // Day regime (quiet / normal / active stretch, big-day odds) — information
  // only, settles by 09:45 and then barely moves.
  const loadDayRegime = useCallback(() => {
    const want = activeIndex;
    fetchDayRegime(want)
      .then((r) => { if (edgeIndexRef.current === want) setDayRegime(r.data); })
      .catch(() => { if (edgeIndexRef.current === want) setDayRegime(null); });
  }, [activeIndex]);
  useEffect(() => { loadDayRegime(); }, [loadDayRegime]);
  usePolling(loadDayRegime, 60000, { immediate: false });

  useEffect(() => {
    // openStream re-creates a stream the browser has given up on (backoff
    // 1 s → 30 s) and verifies the app token after repeated failures, so a
    // lapsed session goes to Login instead of a silently frozen dashboard.
    // staleMs: a stream that sent neither data nor the server's 5 s "ping" for
    // 15 s died without an error (network switch, stalled proxy) — reopen it,
    // which is what a manual reload used to be needed for.
    const opts = { pauseWhenHidden: true, staleMs: STREAM_STALE_MS };
    const stops = [
      // pauseWhenHidden: a background tab drops its three streams and
      // reconnects on return (the server pushes its latest snapshot on connect).
      openStream("/dashboard/sentiment/stream", (data) => {
        setSentiment(data);
        lastSentimentUpdate.current = Date.now();
      }, { ...opts, onStatus: setConnected }),
      openStream("/dashboard/oi/stream", acceptOI, opts),
      openStream("/dashboard/buyer-signal/stream", (data) => {
        setBuyerSignals(data);
        lastBuyerUpdate.current = Date.now();
      }, opts),
    ];
    return () => stops.forEach((stop) => stop());
  }, [acceptOI]);

  // Fallback: while a stream is quiet past its own cadence, poll its endpoint so
  // the screen never freezes until the stream is back (the watchdog above
  // reopens it). Idle when the streams are healthy.
  const streamFallback = useCallback(() => {
    pollIfQuiet("buyer", lastBuyerUpdate, 5000, fetchBuyerSignal, setBuyerSignals);
    pollIfQuiet("oi", lastOiUpdate, 10000, fetchOI, acceptOI);            // a Dhan chain build takes ~5 s
    pollIfQuiet("sentiment", lastSentimentUpdate, 5000, fetchSentiment, setSentiment);
  }, [pollIfQuiet, acceptOI]);
  usePolling(streamFallback, 3000, { immediate: false });

  // One 1-second tick drives the "live · Ns" freshness chip. It intentionally
  // does NOT drive `view`: that is keyed off the minute, so the whole decision
  // tree (and every memoized panel below it) recomputes once a minute instead
  // of once a second.
  const tickClock = useCallback(() => setNowMs(Date.now()), []);
  usePolling(tickClock, 1000);

  const minuteKey = Math.floor(nowMs / 60000);
  const clock = useMemo(() => getIndiaClock(minuteKey * 60000), [minuteKey]);

  const optionData = oi?.indices?.[activeIndex];
  const canonicalSignal = buyerSignals?.signals?.[activeIndex];
  // Holiday / weekend from the buyer-edge calendar for THIS index (a
  // primitive, so the 10 s edge poll does not recompute the whole view).
  const edgeForIndex = buyerEdge && (!buyerEdge.underlying || buyerEdge.underlying === activeIndex) ? buyerEdge : null;
  const dayOff = dayOffFromEdge(edgeForIndex);
  // Stable leaves for the memoised funnel: the rules for this index only
  // change when the 15 s magnitude poll or the index does.
  const edgeRules = useMemo(
    () => (magnitude?.rules || []).filter((r) => !r.underlying || r.underlying === activeIndex),
    [magnitude, activeIndex],
  );
  const lotSize = edgeForIndex?.lot_size || null;
  const view = useMemo(() => {
    const v = applySessionOverlay(applyCanonicalSignal(baseView(optionData, oi?.vix, clock), canonicalSignal), dayOff);
    // The ticket's exits come from the ACTIVE rule for that side when there is
    // one: the direction engine's own ~4% stop / 1.5R target / 5-minute time
    // stop is the exit style the research found loses for a buyer.
    if (v.tradePlan) {
      const rule = ruleForSide(edgeRules, activeIndex, v.tradePlan.optionType);
      if (rule) return { ...v, tradePlan: applyRuleExits(v.tradePlan, rule, lotSize) };
    }
    return v;
  }, [optionData, oi?.vix, clock, canonicalSignal, dayOff, edgeRules, activeIndex, lotSize]);
  // The chip's age is the STALER of the two feeds the verdict is built from (the
  // chain and the buyer signal), so a frozen panel shows as DELAYED, not LIVE.
  // nowMs ticks once a second, so a push that lands mid-tick can be "newer"
  // than the clock — clamp so the freshness chip never reads a negative age.
  const dataAt = Math.min(lastOiUpdate.current, lastBuyerUpdate.current);
  const age = dataAt ? Math.max(0, Math.floor((nowMs - dataAt) / 1000)) : null;
  const delayed = age != null && age * 1000 > STREAM_STALE_MS;
  const live = connected && sentiment?.live && view.health?.ready && !delayed;
  const selectedSeries = premiumSeries[activeIndex] || [];
  // trader.open_trades lists BOTH books (App.jsx merges /autotrade/positions).
  const indexTrades = (trader?.open_trades || []).filter((tr) => tr.underlying === activeIndex);
  const openPositions = indexTrades.length;
  // Manual rows are managed on the Manual Trader; everything else on Autotrader.
  const manageTo = indexTrades.length && indexTrades.every((tr) => managePathFor(tr) === "/manual-trader") ? "/manual-trader" : "/autotrader";
  const systemAlertCount = alertsFeed?.alerts?.length || 0;
  const trendForIndex = trendRegime?.underlying === activeIndex ? trendRegime : null;
  const regimeForIndex = dayRegime && (!dayRegime.underlying || dayRegime.underlying === activeIndex) ? dayRegime : null;
  const edgeCalendar = edgeForIndex?.calendar || null;
  // The verdict in one word, for the desktop alert and the index switch.
  const verdictWord = useMemo(
    () => simpleAction(edgeForIndex, view, simpleRegime(trendForIndex, view).word).word,
    [edgeForIndex, view, trendForIndex],
  );

  const toggleDesktopAlerts = useCallback(async () => {
    if (desktopAlerts) { setDesktopAlerts(false); return; }
    if (!("Notification" in window)) return;
    let permission = window.Notification.permission;
    if (permission === "default") permission = await window.Notification.requestPermission();
    if (permission === "granted") setDesktopAlerts(true);
  }, [desktopAlerts]);

  // One notification + a short tone the moment the verdict turns to BUY CALL
  // or BUY PUT (and again only after it has left that state).
  useEffect(() => {
    if (view.sessionClosed || !desktopAlerts || !("Notification" in window) || window.Notification.permission !== "granted") return;
    const buy = verdictWord === "BUY CALL" || verdictWord === "BUY PUT";
    const fingerprint = buy ? `${activeIndex}-${verdictWord}` : "";
    if (!buy) { lastDesktopAlert.current = ""; return; }
    if (lastDesktopAlert.current === fingerprint) return;
    lastDesktopAlert.current = fingerprint;
    const plan = view.tradePlan;
    try {
      // mobile Chrome has Notification but throws on `new Notification` (service-worker only)
      new window.Notification(`${activeIndex}: ${verdictWord}`, {
        body: plan ? `${activeIndex} ${fmtPrice(plan.strike)} ${plan.optionType} · buy ≤ ₹${fmtPrice(plan.entry, 1)} · stop ₹${fmtPrice(plan.stop, 1)}${plan.rule ? ` (−${fmtPrice(plan.rule.sl)}%)` : ""}` : "Open the dashboard for the ticket",
        tag: `option-buyer-${activeIndex}`,
      });
    } catch {}
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (AudioContext) {
        const context = new AudioContext();
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        oscillator.frequency.value = 760;
        gain.gain.setValueAtTime(0.05, context.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + 0.18);
        oscillator.connect(gain); gain.connect(context.destination);
        oscillator.start(); oscillator.stop(context.currentTime + 0.18);
      }
    } catch {}
  }, [activeIndex, desktopAlerts, verdictWord, view.sessionClosed, view.tradePlan]);

  // "Place this on Manual Trader": carry the ticket across as a prefill. The
  // stop and target travel as % of the entry — the fields the manual ticket
  // understands — so the trader lands on a filled form, not a blank one.
  const tradeOnManual = useCallback((plan) => {
    if (!plan) return;
    const entry = Number(plan.entry) || 0;
    const pctOf = (x) => (entry > 0 && x != null ? Math.round((Math.abs(Number(x) - entry) / entry) * 100) : null);
    // With a rule behind the ticket the stop is the rule's % and there is no
    // fixed target (null keeps the manual default — the manual book needs one).
    const slPct = plan.rule ? Math.round(plan.rule.sl) : pctOf(plan.stop);
    const targetPct = plan.rule ? (plan.targetPct != null ? Math.round(plan.targetPct) : null) : pctOf(plan.target1);
    navigate("/manual-trader", {
      state: {
        prefill: {
          underlying: activeIndex, option_type: plan.optionType, strike: plan.strike,
          sl_pct: slPct, target_pct: targetPct, lots: plan.lots || null,
          source: "dashboard",
        },
      },
    });
  }, [navigate, activeIndex]);

  const cockpitRow = (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
      <Panel title="Open position" eyebrow="Protect premium after entry" icon={Crosshair}
        action={<a href={manageTo} onClick={(e) => { e.preventDefault(); navigate(manageTo); }} className="flex items-center gap-1 text-[10px] font-bold text-cyan-400">Manage position <ChevronRight className="h-3 w-3" /></a>}
        className="xl:col-span-5" testId="post-entry-cockpit">
        <CockpitRiskBand status={trader} />
        <PostEntryCockpit trades={trader?.open_trades} activeIndex={activeIndex} view={view} session={edgeForIndex?.session} minuteOfDay={clock.minuteOfDay} />
      </Panel>
      <Panel title="ATM premium & spot" eyebrow="Premium vs its VWAP · spot" icon={Activity}
        className="xl:col-span-7" testId="premium-pulse">
        <PremiumTracker series={selectedSeries} data={optionData} view={view} />
      </Panel>
    </div>
  );

  return (
    <div className="option-buyer-dashboard route-enter min-h-[calc(100vh-7rem)] text-mid">
      <div className="stagger mx-auto max-w-[1680px] space-y-4">
        <header className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-cyan-500/25 bg-cyan-400/10"><Zap className="h-5 w-5 text-cyan-300" /></div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-black tracking-tight text-hi">Option Buyer</h1>
                <span className="rounded bg-cyan-400/10 px-1.5 py-0.5 text-[10px] font-black uppercase tracking-widest text-cyan-300">Intraday</span>
              </div>
              <p className="text-[11px] text-slate-500">Should I buy an option now, and which one?</p>
              <div className="mt-1.5"><ExpiryStrip calendar={edgeCalendar} events={events} /></div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Index switch, each with its own direction verdict: the other
                index may have the cleaner setup. */}
            <div className="flex rounded-xl border border-line bg-surface-2 p-1" role="group" aria-label="Index">
              {["NIFTY", "BANKNIFTY"].map((key) => {
                const sig = buyerSignals?.signals?.[key];
                const dv = directionVerdict(sig?.action?.side, view.sessionClosed);
                const active = activeIndex === key;
                return (
                  <button key={key} type="button" onClick={() => setActiveIndex(key)} aria-pressed={active}
                    className={`flex flex-col items-start rounded-lg px-3 py-1 text-left transition ${active ? "bg-cyan-400 text-on-dark" : "text-slate-500 hover:text-hi"}`}>
                    <span className="text-[11px] font-black leading-4">{key === "BANKNIFTY" ? "BANK NIFTY" : "NIFTY 50"}</span>
                    <span className={`text-[10px] font-bold leading-3 ${active ? "opacity-80" : INDEX_INK[dv.tone] || INDEX_INK.slate}`}>{dv.word}</span>
                  </button>
                );
              })}
            </div>
            <div data-testid="feed-status" className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-[10px] font-bold ${live ? "border-emerald-500/25 bg-emerald-500/[.06] text-emerald-300" : delayed && !view.sessionClosed ? "border-amber-500/25 bg-amber-500/[.06] text-amber-300" : "border-slate-800 bg-slate-900 text-slate-500"}`}>
              {view.sessionClosed ? <Clock3 className="h-3.5 w-3.5" /> : <Radio className={`h-3.5 w-3.5 ${live ? "animate-pulse" : ""}`} />}{view.sessionClosed ? (view.closedLabel === "Market holiday" ? "MARKET HOLIDAY" : "MARKET CLOSED") : live ? `LIVE · ${age ?? 0}s` : delayed ? `DELAYED · ${age}s` : "DATA BLOCKED"}
            </div>
            <div className="rounded-xl border border-line bg-surface-2 px-3 py-2 text-[10px] font-bold text-slate-400"><Clock3 className="mr-1.5 inline h-3.5 w-3.5" />{clock.label} IST</div>
          </div>
        </header>

        {/* Kill switch / stale monitor: one red line that no tab can hide. */}
        <SystemAlertStrip feed={alertsFeed} />

        {/* Where in the trading day we are: opening noise, entry window, midday
            theta, cutoff, square-off — from the same settings the rules use. */}
        {!view.sessionClosed && <SessionTimeline session={edgeForIndex?.session} minuteOfDay={clock.minuteOfDay} />}

        {/* THE answer: four gates → one verdict → the ticket or what to change. */}
        <BuyerFunnel view={view} edge={edgeForIndex} trend={trendForIndex} regime={regimeForIndex}
          rules={edgeRules} status={trader} activeIndex={activeIndex} onTrade={tradeOnManual}
          alertsEnabled={desktopAlerts} onToggleAlerts={toggleDesktopAlerts} />

        {/* An open position is never hidden by the simple view. */}
        {openPositions > 0 && cockpitRow}

        {/* The screen in two halves: CALL left, PUT right — what each costs and
            whether the move can pay for it; the direction votes fold away. */}
        <CallPutSplit view={view} optionData={optionData} edge={edgeForIndex} trend={trendForIndex} />

        {/* Everything else, on the same screen — no switch, no tabs. */}
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-12" data-testid="section-decision">
          <Panel title="Entry checklist" eyebrow="Every gate behind the verdict" icon={ListChecks} className="xl:col-span-5" testId="buyer-checklist">
            <BuyerChecklist view={view} />
          </Panel>
          <Panel title="Best strikes to buy" eyebrow="Liquidity · spread · premium vs VWAP · delta · theta" icon={Trophy} className="xl:col-span-7" testId="strike-selector">
            <SmartStrikeSelector data={optionData} preferredSide={view.side} />
          </Panel>
        </div>

        {/* The premium trace sits beside the cockpit while a position is open. */}
        {openPositions === 0 && (
          <Panel title="ATM premium & spot" eyebrow="Premium vs its VWAP · spot" icon={Activity} testId="premium-pulse">
            <PremiumTracker series={selectedSeries} data={optionData} view={view} />
          </Panel>
        )}

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-12" data-testid="section-chain">
          <Panel title="Option chain" eyebrow="Where the big positions sit · ceiling and floor" icon={BarChart3} className="xl:col-span-8"
            action={<span className="text-[11px] text-slate-600">{optionData?.atm ? `ATM ${fmtPrice(optionData.atm)}` : "No chain"}</span>} testId="option-chain-heatmap">
            <StrikeHeatmap data={optionData} />
          </Panel>
          <Panel title="Volatility smile" eyebrow="Implied volatility by strike · calls vs puts" icon={Activity} className="xl:col-span-4" testId="iv-smile">
            <Suspense fallback={<div style={{ height: 150 }} className="flex items-center justify-center text-[11px] text-slate-600">Loading smile…</div>}>
              <IvSmile chain={optionData?.chain} atm={optionData?.atm} />
            </Suspense>
          </Panel>
        </div>

        <Panel title="System alerts" eyebrow="Kill switch · monitor · escalations" icon={BellRing}
          action={systemAlertCount > 0 ? <span className="rounded-full bg-cyan-500/20 px-2 text-[11px] font-black text-cyan-300">{systemAlertCount}</span> : null} testId="alerts-panel">
          <AlertsPanel feed={alertsFeed} />
        </Panel>

        <footer className="flex flex-col items-center justify-between gap-2 border-t border-slate-800/70 px-1 pt-3 text-[10px] text-slate-600 sm:flex-row">
          <p>Decision support, not a guaranteed probability. The live rules trade on their own conditions.</p>
          <div className="flex items-center gap-3"><span>{trader?.is_paper === false ? "LIVE" : "PAPER"} · {trader?.autopilot_running ? "AUTOPILOT ON" : "AUTOPILOT OFF"}</span><a href="#option-chain" onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid="option-chain-heatmap"]')?.scrollIntoView({ behavior: "smooth" }); }} className="flex items-center gap-1 font-bold text-cyan-400 hover:text-cyan-300">Jump to option chain <ChevronRight className="h-3 w-3" /></a></div>
        </footer>
      </div>
    </div>
  );
}
